import { useCallback, useEffect, useState } from "react";
import Modal from "components/Modal";
import { useBillingNavigation } from "./hooks/useBillingNavigation";
import Button from "components/Button";
import { ReactComponent as ContactSalesIcon } from "assets/icons/contact-sales-icon.svg";
import ServiceScriptProvider from "providers/ServicesProvider/ServiceScriptProvider";
import Spinner from "components/Spinner";
import { trackBillingV2ModalOpened, trackBillingV2ContactSalesFormSubmitted } from "utils/Tracking";

interface IHubSpotWindow {
  hbspt?: {
    forms?: {
      create?: (options: {
        region: string;
        portalId: string;
        formId: string;
        target?: string;
        onFormSubmitted?: () => void;
      }) => void;
    };
  };
}

// HubSpot form content with submission callback
function HubSpotFormContent({ onFormSubmitted }: { onFormSubmitted: () => void }) {
  const PLACEHOLDER_DIV_CLASS = "hsform-contact-sales-container";
  const RENDER_SCRIPT_ID = "hsform-contact-sales-scriptlet";
  const [isLoading, setIsLoading] = useState(true);

  const insertHSFormRenderScript = useCallback(() => {
    if (!document.getElementById(RENDER_SCRIPT_ID) && (window as IHubSpotWindow).hbspt) {
      // Store callback on window for the script to access
      (window as IHubSpotWindow & { __hsFormCallback?: () => void }).__hsFormCallback = onFormSubmitted;

      const renderScript = document.createElement("script");
      renderScript.id = RENDER_SCRIPT_ID;
      renderScript.type = "text/javascript";
      renderScript.innerText = `
        hbspt.forms.create({
          region: "na1",
          portalId: "6840215",
          formId: "b79d73cb-dbd2-4a4e-8f1d-570698d23064",
          target: ".${PLACEHOLDER_DIV_CLASS}",
          onFormReady: function() {
            if (window.__hsFormLoadedCallback) {
              window.__hsFormLoadedCallback();
            }
          },
          onFormSubmitted: function() {
            if (window.__hsFormCallback) {
              window.__hsFormCallback();
            }
          }
        });
      `;

      document.getElementsByClassName(PLACEHOLDER_DIV_CLASS)[0]?.appendChild(renderScript);
    }
  }, [onFormSubmitted]);

  const removeHSFormRenderScript = useCallback(() => {
    document.getElementById(RENDER_SCRIPT_ID)?.remove();
    // Clean up window callbacks only - let React handle DOM cleanup
    delete (window as IHubSpotWindow & { __hsFormCallback?: () => void }).__hsFormCallback;
    delete (window as IHubSpotWindow & { __hsFormLoadedCallback?: () => void }).__hsFormLoadedCallback;
  }, []);

  const hubSpotScript = {
    name: "HubSpot Form Embed Script",
    domId: "hsforms-embed-script",
    src: "//js.hsforms.net/forms/embed/v2.js",
    onLoadCallback: insertHSFormRenderScript,
    onUnloadCallback: removeHSFormRenderScript
  };

  useEffect(() => {
    // Store loaded callback on window
    (window as IHubSpotWindow & { __hsFormLoadedCallback?: () => void }).__hsFormLoadedCallback = () => {
      setIsLoading(false);
    };

    insertHSFormRenderScript();

    return () => {
      removeHSFormRenderScript();
    };
  }, [insertHSFormRenderScript, removeHSFormRenderScript]);

  return (
    <>
      <ServiceScriptProvider {...hubSpotScript} />
      <div className="flex flex-col p-5 gap-y-6">
        <div className="text-gray">
          Interested in our Enterprise plan? Fill out this form and our sales team will be in touch.
        </div>
        <div className="relative min-h-[500px]">
          {/* Loading overlay - positioned above HubSpot container */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="flex flex-col items-center gap-3">
                <Spinner size="MD" />
                <span className="text-sm text-gray-500">Loading form...</span>
              </div>
            </div>
          )}
          {/* HubSpot form container - always rendered, HubSpot injects here */}
          <div className={PLACEHOLDER_DIV_CLASS} />
        </div>
      </div>
    </>
  );
}

// Success confirmation content
function SuccessContent({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Title */}
      <h2 className="text-2xl font-semibold text-center">
        Thank you for your interest!
      </h2>

      {/* Subtitle */}
      <p className="text-sm text-center text-gray-600">
        Our sales team will reach out within 2 business days
      </p>

      {/* Icon illustration */}
      <div className="flex justify-center pb-4">
        <ContactSalesIcon className="w-72 h-72" />
      </div>

      {/* Done button */}
      <Button
        text="DONE"
        onClick={onClose}
        className="button button-primary w-full"
        size="MD"
      />
    </div>
  );
}

export default function ContactSalesModal() {
  const { goBack } = useBillingNavigation();
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => { trackBillingV2ModalOpened('contact_sales'); }, []);

  const handleFormSubmitted = useCallback(() => {
    trackBillingV2ContactSalesFormSubmitted();
    setFormSubmitted(true);
  }, []);

  return (
    <Modal
      // TODO why do we hide the title after submission?
      headline={formSubmitted ? undefined : "Contact Sales"}
      onClose={goBack}
    >
      {formSubmitted ? (
        <SuccessContent onClose={goBack} />
      ) : (
        <HubSpotFormContent onFormSubmitted={handleFormSubmitted} />
      )}
    </Modal>
  );
}
